import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/**
 * Cloudflare Turnstile bot challenge.
 *
 * The SITE key is fetched from the `security-config` edge function (the secret
 * key never reaches the browser). The token produced here is ALWAYS re-verified
 * server-side inside the `crm-capture` edge function — the widget alone is not
 * treated as proof.
 *
 * If Turnstile is not configured the component renders nothing and calls
 * onVerify('') so existing forms keep working exactly as before.
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

let cachedSiteKey: string | null | undefined;
let scriptPromise: Promise<void> | null = null;

export const loadTurnstileConfig = async (): Promise<string | null> => {
  if (cachedSiteKey !== undefined) return cachedSiteKey ?? null;
  try {
    const { data } = await supabase.functions.invoke('security-config', { body: {} });
    cachedSiteKey = data?.turnstile?.siteKey || null;
  } catch {
    cachedSiteKey = null;
  }
  return cachedSiteKey ?? null;
};

const loadScript = () => {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve) => {
    if (window.turnstile) return resolve();
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
  return scriptPromise;
};

interface Props {
  action: string;
  onVerify: (token: string) => void;
  className?: string;
  label?: string;
}

const Turnstile: React.FC<Props> = ({ action, onVerify, className = '', label }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const widget = useRef<string | null>(null);
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const siteKey = await loadTurnstileConfig();
      if (cancelled) return;
      if (!siteKey) {
        setEnabled(false);
        onVerify('');
        return;
      }
      setEnabled(true);
      await loadScript();
      if (cancelled || !ref.current || !window.turnstile) {
        onVerify('');
        return;
      }
      try {
        widget.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action: action.slice(0, 32),
          theme: 'light',
          callback: (token: string) => {
            setReady(true);
            onVerify(token);
          },
          'expired-callback': () => {
            setReady(false);
            onVerify('');
          },
          'error-callback': () => {
            setReady(false);
            onVerify('');
          },
        });
      } catch {
        onVerify('');
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  if (enabled === false) return null;

  return (
    <div className={className}>
      <div ref={ref} />
      {label && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <ShieldCheck className={`h-3.5 w-3.5 ${ready ? 'text-emerald-500' : 'text-slate-400'}`} />
          {label}
        </p>
      )}
    </div>
  );
};

export default Turnstile;
